"use client";

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { ONBOARDING_FLOW, OnboardingStep, PREV_ONBOARDING_STEP_KEY } from "~/utils/onboarding";

interface OnboardingContextType {
  prevStep: OnboardingStep | null;
  setPrevOnboardingStep: (step: OnboardingStep | null) => void;
  isOnboarding: boolean;
  startOnboarding: () => void;
  completeOnboarding: () => void;
  progress: {
    currentStepNumber: number;
    totalSteps: number;
    progress: number;
  };
}

export function calculateProgress(prevStep: OnboardingStep): {
  currentStepNumber: number;
  totalSteps: number;
  progress: number;
} {
  const currentIndex = ONBOARDING_FLOW.findIndex((step) => step.id === prevStep);
  const totalSteps = ONBOARDING_FLOW.length;

  return {
    currentStepNumber: currentIndex + 1,
    totalSteps,
    progress: ((currentIndex + 1) / totalSteps) * 100,
  };
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const validateStoredStep = (step: string | null): OnboardingStep | null => {
    if (!step || !Object.values(OnboardingStep).includes(step as OnboardingStep)) {
      sessionStorage.removeItem(PREV_ONBOARDING_STEP_KEY);
      return null;
    }
    return step as OnboardingStep;
  };

  const [prevStep, setPrevOnboardingStep] = useState<OnboardingStep | null>(null);
  const [isOnboarding, setIsOnboarding] = useState(false);

  useEffect(() => {
    const storedStep = sessionStorage.getItem(PREV_ONBOARDING_STEP_KEY);
    const validatedStep = validateStoredStep(storedStep);
    setPrevOnboardingStep(validatedStep);
    setIsOnboarding(validatedStep !== null);
  }, []);

  useEffect(() => {
    if (prevStep) {
      sessionStorage.setItem(PREV_ONBOARDING_STEP_KEY, prevStep);
    }
  }, [prevStep]);

  const startOnboarding = () => {
    setIsOnboarding(true);
    setPrevOnboardingStep(OnboardingStep.CREATE_ACCOUNT);
  };

  const completeOnboarding = () => {
    setIsOnboarding(false);
    sessionStorage.removeItem(PREV_ONBOARDING_STEP_KEY);
    setPrevOnboardingStep(null);
  };

  const progress = useMemo(() => {
    if (!prevStep)
      return {
        currentStepNumber: 0,
        totalSteps: ONBOARDING_FLOW.length,
        progress: 0,
      };
    return calculateProgress(prevStep);
  }, [prevStep]);

  return (
    <OnboardingContext.Provider
      value={{
        prevStep,
        setPrevOnboardingStep,
        isOnboarding,
        startOnboarding,
        completeOnboarding,
        progress,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error("useOnboarding must be used within a OnboardingProvider");
  }
  return context;
}
